'use client';

import { useId } from 'react';
import type { FormEventHandler } from 'react';
import { Switch } from '@1debit/chimekit';
import { CalculatorActions } from 'components/Widget/shared/CalculatorActions';
import { CALCULATOR_FORM_CARD } from 'components/Widget/shared/cardStyles';
import { CalculatorInput } from 'components/Widget/CalculatorInput/CalculatorInput';
import { HintDisclosure } from 'components/Widget/shared/HintDisclosure';
import { RadioField } from 'components/Widget/shared/RadioField';
import type { NumberFieldProps } from 'components/Widget/shared/useNumberFields';
import type { CurrencyFieldProps } from 'components/Widget/shared/useCurrencyFields';
import type { PercentFieldProps } from 'components/Widget/shared/usePercentFields';
import {
  EXTRA_PAYMENT_FIELD,
  EXTRA_PAYMENT_INPUT_LABEL,
  EXTRA_PAYMENT_SUFFIX,
  FORM_LABEL,
  getRateType,
  LOAN_AMOUNT_FIELD,
  LOAN_TERM_FIELD,
  RATE_FIELD,
  RATE_TYPE_LEGEND,
  RATE_TYPE_OPTIONS,
  TERM_UNIT_LEGEND,
  TERM_UNIT_OPTIONS,
} from './constants';
import type { LoanFormErrors, RateType, TermUnit } from './types';

interface LoanCalculatorFormProps {
  errors: LoanFormErrors;
  currencyFieldProps: CurrencyFieldProps;
  rateFieldProps: PercentFieldProps;
  termFieldProps: NumberFieldProps;
  rateType: RateType;
  termUnit: TermUnit;
  isExtraPaymentOn: boolean;
  onRateTypeChange: (key: RateType) => void;
  onTermUnitChange: (key: TermUnit) => void;
  onExtraPaymentToggle: (isOn: boolean) => void;
  onCalculate: () => void;
  onReset: () => void;
}

export const LoanCalculatorForm = ({
  errors,
  currencyFieldProps,
  rateFieldProps,
  termFieldProps,
  rateType,
  termUnit,
  isExtraPaymentOn,
  onRateTypeChange,
  onTermUnitChange,
  onExtraPaymentToggle,
  onCalculate,
  onReset,
}: LoanCalculatorFormProps) => {
  const extraPaymentSwitchId = useId();
  const rateLabel = getRateType(rateType).label;

  const handleSubmit: FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault();
    onCalculate();
  };

  return (
    <form aria-label={FORM_LABEL} className={CALCULATOR_FORM_CARD} onSubmit={handleSubmit}>
      <CalculatorInput {...currencyFieldProps(LOAN_AMOUNT_FIELD, errors.loanAmount)} />

      <RadioField
        legend={RATE_TYPE_LEGEND}
        options={RATE_TYPE_OPTIONS}
        value={rateType}
        onChange={option => onRateTypeChange(option.key)}
      />

      {/* Keyed on `RATE_FIELD` so the stored value survives a rate-type switch; only the label follows it. */}
      <CalculatorInput {...rateFieldProps(RATE_FIELD, errors.rate)} label={rateLabel} />

      <CalculatorInput {...termFieldProps(LOAN_TERM_FIELD, errors.term)} />

      <RadioField
        legend={TERM_UNIT_LEGEND}
        hideLegend
        options={TERM_UNIT_OPTIONS}
        value={termUnit}
        onChange={option => onTermUnitChange(option.key)}
      />

      <hr className="border-content-tertiary/20" />

      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <Switch
            id={extraPaymentSwitchId}
            value={isExtraPaymentOn}
            onValueChange={onExtraPaymentToggle}
            aria-labelledby={`${extraPaymentSwitchId}-label`}
          />
          <label id={`${extraPaymentSwitchId}-label`} htmlFor={extraPaymentSwitchId} className="text-body-saans">
            {EXTRA_PAYMENT_FIELD.label} (optional)
          </label>
          <HintDisclosure
            hintId={`${extraPaymentSwitchId}-hint`}
            label={EXTRA_PAYMENT_FIELD.label}
            hint={EXTRA_PAYMENT_FIELD.hint}
          />
        </div>

        {isExtraPaymentOn && (
          <CalculatorInput
            {...currencyFieldProps(EXTRA_PAYMENT_FIELD, errors.extraPayment)}
            label={EXTRA_PAYMENT_INPUT_LABEL}
            suffixLabel={EXTRA_PAYMENT_SUFFIX}
            // The toggle above owns the hint; rendering it here too puts the same sentence on screen twice.
            hint={undefined}
          />
        )}
      </div>

      <CalculatorActions onReset={onReset} />
    </form>
  );
};
